import PropTypes from 'prop-types';
import React from 'react';
import StyledSystemPropTypes from '@styled-system/prop-types';
import { motion, stagger } from 'motion/react';

import { Box, Flex } from '../grid';
import { Button } from '../button/new';
import { CategoryCard } from './category-card';

const containerVariants = {
  offscreen: { opacity: 1 },
  onscreen: {
    opacity: 1,
    delay: 5,
    transition: {
      delayChildren: stagger(0.1),
    },
  },
};

const cardVariants = {
  offscreen: {
    opacity: 0,
    y: '50%',
  },
  onscreen: {
    opacity: 1,
    y: 0,
    transition: {
      opacity: {
        duration: 0.15,
        ease: 'easeOut',
      },
      y: {
        duration: 0.3,
        ease: 'easeOut',
      },
    },
  },
};

const CategoryGrid = ({ categories, onShowMore, showMoreText, buttonProps, ...rest }) => {
  return (
    <Flex flexDirection="column" width="100%" alignItems="center" {...rest}>
      <motion.div
        variants={containerVariants}
        initial="offscreen"
        whileInView="onscreen"
        viewport={{ once: true, amount: 0.5 }}
      >
        <Box
          display="grid"
          gridTemplateColumns={{ xs: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }}
          gridTemplateRows={{
            xs: `repeat(${Math.ceil(categories.length / 2)}, 1fr)`,
            lg: `repeat(${Math.ceil(categories.length / 3)}, 1fr)`,
          }}
          gridAutoFlow="column"
          gap={{ xs: '16px', lg: '24px' }}
          width="100%"
          maxWidth="1140px"
          pb={{ xs: '36px', lg: '72px' }}
        >
          {categories?.map((category, index) => {
            // Calculate stagger positioning with column-first flow
            const columnMobile = Math.floor(index / 3); // Which column (0, 1, 2...)
            const columnDesktop = Math.floor(index / 2); // Which column (0, 1, 2...)
            const shouldStaggerMobile = columnMobile % 2 === 0; // Stagger every other column
            const shouldStaggerDesktop = columnDesktop % 3 === 0 || columnDesktop % 3 === 2; // Stagger first and third columns

            return (
              <motion.div variants={cardVariants}>
                <CategoryCard
                  {...category}
                  href={category?.genericLink?.url || null}
                  transform={{
                    xs: shouldStaggerMobile ? 'translateY(36px)' : 'translateY(0)',
                    lg: shouldStaggerDesktop ? 'translateY(72px)' : 'translateY(0)',
                  }}
                />
              </motion.div>
            );
          })}
        </Box>
      </motion.div>

      {showMoreText && (
        <>
          <Button
            display={{ xs: 'flex', lg: 'none' }}
            variant="primary"
            size="medium"
            widthType="fixed"
            onClick={onShowMore}
            mt={{ xs: '32px', lg: '0px' }}
            {...buttonProps}
          >
            {showMoreText}
          </Button>

          <Button
            display={{ xs: 'none', lg: 'flex' }}
            variant="primary"
            size="tall"
            widthType="fixed"
            onClick={onShowMore}
            mt={{ xs: '32px', lg: '0px' }}
            {...buttonProps}
          >
            {showMoreText}
          </Button>
        </>
      )}
    </Flex>
  );
};

CategoryGrid.propTypes = {
  ...StyledSystemPropTypes.space,
  ...StyledSystemPropTypes.layout,
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      image: PropTypes.object,
      href: PropTypes.string,
      onClick: PropTypes.func,
    })
  ),
  buttonProps: PropTypes.object,
  onShowMore: PropTypes.func,
  showMoreText: PropTypes.string,
};

CategoryGrid.defaultProps = {
  categories: [],
  onShowMore: undefined,
  showMoreText: 'SEE MORE',
  buttonProps: {},
};

export { CategoryGrid };
